import { computed, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';

function resolveSeasonMedia(member, seasonKey) {
    const bySeason = member?.mediaBySeason || {};
    const keys = Object.keys(bySeason);
    if (!keys.length) {
        return null;
    }

    if (seasonKey === 'all') {
        return null;
    }

    return bySeason[seasonKey]
        ?? bySeason[String(seasonKey)]
        ?? bySeason[Number(seasonKey)]
        ?? null;
}

function memberHasPositionId(member, seasonKey, positionId) {
    const target = Number(positionId);

    if (seasonKey !== 'all') {
        const media = resolveSeasonMedia(member, seasonKey);
        return media?.position_id != null && Number(media.position_id) === target;
    }

    return Object.values(member.mediaBySeason || {}).some(
        (media) => media?.position_id != null && Number(media.position_id) === target,
    );
}

function defaultSeasonKey(seasons) {
    const active = (seasons || []).find((season) => season.is_active);
    return active ? String(active.id) : 'all';
}

function collectPositionEntries(member, seasonKey, byId) {
    if (seasonKey === 'all') {
        Object.values(member.mediaBySeason || {}).forEach((media) => {
            if (media?.position_id == null || !media?.position) {
                return;
            }
            const id = Number(media.position_id);
            if (!byId.has(id)) {
                byId.set(id, media.position);
            }
        });
        return;
    }

    const media = resolveSeasonMedia(member, seasonKey);
    if (media?.position_id == null || !media?.position) {
        return;
    }
    const id = Number(media.position_id);
    if (!byId.has(id)) {
        byId.set(id, media.position);
    }
}

/**
 * Shared filter/display logic for /players and /coaches directories.
 */
export function useMemberDirectory(props, { emptyLabel = 'Не найдено' } = {}) {
    const page = usePage();
    const searchQuery = ref('');
    const selectedTeam = ref('all');
    const selectedPosition = ref('all');
    const selectedNationality = ref('all');
    const selectedSeason = ref(defaultSeasonKey(props.seasons));

    watch(
        () => selectedSeason.value,
        (season) => {
            if (season === 'all') {
                selectedTeam.value = 'all';
            }
        },
    );

    const showTeamFilter = computed(() => selectedSeason.value !== 'all');
    const showSeasonMeta = computed(() => selectedSeason.value !== 'all');

    const membersForSeason = computed(() => {
        const members = props.members || [];
        if (selectedSeason.value === 'all') {
            return members;
        }

        const seasonId = Number(selectedSeason.value);
        return members.filter((member) =>
            (member.season_ids || []).some((id) => Number(id) === seasonId),
        );
    });

    const teamsList = computed(() => {
        if (selectedSeason.value === 'all') {
            return [];
        }

        const byId = new Map();
        membersForSeason.value.forEach((member) => {
            const media = resolveSeasonMedia(member, selectedSeason.value);
            if (media?.team_id == null || !media?.team) {
                return;
            }
            const id = Number(media.team_id);
            if (!byId.has(id)) {
                byId.set(id, media.team);
            }
        });

        return [...byId.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([id, name]) => ({ id, name }));
    });

    const positionsList = computed(() => {
        const byId = new Map();
        membersForSeason.value.forEach((member) => {
            collectPositionEntries(member, selectedSeason.value, byId);
        });

        return [...byId.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([id, name]) => ({ id, name }));
    });

    const nationalitiesList = computed(() => {
        const codes = new Set();
        membersForSeason.value.forEach((member) => {
            if (member.nationality) {
                codes.add(String(member.nationality));
            }
        });

        const catalog = page.props.nationalityOptions || [];
        const byCode = new Map(catalog.map((option, index) => [option.value, { ...option, sort: index }]));

        return [...codes]
            .map((code) => {
                const meta = byCode.get(code);
                return {
                    value: code,
                    label: meta?.label || code,
                    sort: meta?.sort ?? Number.MAX_SAFE_INTEGER,
                };
            })
            .sort((a, b) => a.sort - b.sort || a.value.localeCompare(b.value));
    });

    watch(nationalitiesList, (list) => {
        if (selectedNationality.value === 'all') {
            return;
        }
        if (!list.some((item) => item.value === selectedNationality.value)) {
            selectedNationality.value = 'all';
        }
    });

    watch(teamsList, (list) => {
        if (selectedTeam.value === 'all') {
            return;
        }
        if (!list.some((item) => String(item.id) === String(selectedTeam.value))) {
            selectedTeam.value = 'all';
        }
    });

    watch(positionsList, (list) => {
        if (selectedPosition.value === 'all') {
            return;
        }
        if (!list.some((item) => String(item.id) === String(selectedPosition.value))) {
            selectedPosition.value = 'all';
        }
    });

    const isFiltered = computed(() =>
        searchQuery.value !== ''
        || selectedTeam.value !== 'all'
        || selectedPosition.value !== 'all'
        || selectedNationality.value !== 'all'
        || selectedSeason.value !== 'all',
    );

    const filteredMembers = computed(() => {
        let result = props.members || [];

        if (selectedSeason.value !== 'all') {
            const seasonId = Number(selectedSeason.value);
            result = result.filter((member) =>
                (member.season_ids || []).some((id) => Number(id) === seasonId),
            );
        }

        if (selectedSeason.value !== 'all' && selectedTeam.value !== 'all') {
            const teamId = Number(selectedTeam.value);
            result = result.filter((member) => {
                const media = resolveSeasonMedia(member, selectedSeason.value);
                return media?.team_id != null && Number(media.team_id) === teamId;
            });
        }

        if (selectedPosition.value !== 'all') {
            result = result.filter((member) =>
                memberHasPositionId(member, selectedSeason.value, selectedPosition.value),
            );
        }

        if (selectedNationality.value !== 'all') {
            const code = selectedNationality.value;
            result = result.filter((member) => String(member.nationality || '') === code);
        }

        if (searchQuery.value) {
            const query = searchQuery.value.toLowerCase();
            result = result.filter((member) => {
                if (member.full_name?.toLowerCase().includes(query)) {
                    return true;
                }
                if (selectedSeason.value === 'all') {
                    return false;
                }
                const media = resolveSeasonMedia(member, selectedSeason.value);
                return media?.team?.toLowerCase().includes(query);
            });
        }

        return result;
    });

    const displayMembers = computed(() =>
        filteredMembers.value.map((member) => {
            if (selectedSeason.value === 'all') {
                return {
                    id: member.id,
                    url: member.url,
                    full_name: member.full_name,
                    surname: member.surname,
                    first_name: member.first_name,
                    middle_name: member.middle_name,
                    middle_name_visible: member.middle_name_visible,
                    photo_url: member.photo_url,
                    status: member.status || null,
                    team: null,
                    team_logo_url: null,
                    team_url: null,
                    team_short_name: null,
                    number: null,
                    position: null,
                    primary_color: null,
                    secondary_color: null,
                };
            }

            const media = resolveSeasonMedia(member, selectedSeason.value);
            return {
                id: member.id,
                url: member.url,
                full_name: member.full_name,
                surname: member.surname,
                first_name: member.first_name,
                middle_name: member.middle_name,
                middle_name_visible: member.middle_name_visible,
                photo_url: media?.photo_url ?? member.photo_url,
                status: member.status || null,
                team: media?.team || null,
                team_logo_url: media?.team_logo_url || null,
                team_url: media?.team_url || null,
                team_short_name: media?.team_short_name || null,
                number: media?.number ?? null,
                position: media?.position || null,
                primary_color: media?.primary_color || null,
                secondary_color: media?.secondary_color || null,
            };
        }),
    );

    return {
        searchQuery,
        selectedTeam,
        selectedPosition,
        selectedNationality,
        selectedSeason,
        showTeamFilter,
        showSeasonMeta,
        teamsList,
        positionsList,
        nationalitiesList,
        isFiltered,
        filteredMembers,
        displayMembers,
        emptyLabel,
    };
}
